import { create } from 'zustand'
import { toast } from 'react-toastify'
import { TutorialSeries, ALL_TUTORIALS } from '@/components/tutorial/tutorialSeries'

// Module-level spot registry (DOM elements, independent of React tree)
const spotElements = new Map<string, HTMLElement>()

type TutorialCompleteCallback = (seriesId: string) => void | Promise<void>

interface TutorialState {
  activeSeries: TutorialSeries | null
  currentStepIndex: number
  isActive: boolean
  spotsVersion: number
  completedSeriesIds: string[]
  onTutorialComplete: TutorialCompleteCallback | null
  startTutorial: (seriesId: string) => void
  advanceStep: () => void
  endTutorial: () => void
  isTutorialCompleted: (seriesId: string) => boolean
  setCompletedSeriesIds: (ids: string[]) => void
  setOnTutorialComplete: (cb: TutorialCompleteCallback | null) => void
  registerSpot: (id: string, element: HTMLElement) => void
  unregisterSpot: (id: string) => void
  getSpotElement: (id: string) => HTMLElement | undefined
}

export const useTutorial = create<TutorialState>((set, get) => ({
  activeSeries: null,
  currentStepIndex: 0,
  isActive: false,
  spotsVersion: 0,
  completedSeriesIds: [],
  onTutorialComplete: null,

  startTutorial: (seriesId: string) => {
    const series = ALL_TUTORIALS[seriesId]
    if (!series) return
    set({ activeSeries: series, currentStepIndex: 0, isActive: true, spotsVersion: get().spotsVersion + 1 })
  },

  advanceStep: () => {
    const { activeSeries, currentStepIndex, completedSeriesIds, onTutorialComplete } = get()
    if (!activeSeries) return
    const nextIndex = currentStepIndex + 1
    console.log('[Tutorial] advanceStep:', currentStepIndex, '→', nextIndex, 'of', activeSeries.steps.length)
    if (nextIndex >= activeSeries.steps.length) {
      const seriesId = activeSeries.id
      const nextCompleted = completedSeriesIds.includes(seriesId)
        ? completedSeriesIds
        : [...completedSeriesIds, seriesId]
      set({ activeSeries: null, currentStepIndex: 0, isActive: false, completedSeriesIds: nextCompleted })
      toast(activeSeries.completionText, { closeButton: true, autoClose: 4000 })
      if (onTutorialComplete) {
        Promise.resolve(onTutorialComplete(seriesId)).catch(e => {
          console.error('[Tutorial] onTutorialComplete failed', e)
        })
      }
    } else {
      set({ currentStepIndex: nextIndex, spotsVersion: get().spotsVersion + 1 })
      console.log('[Tutorial] now at step', nextIndex, ':', activeSeries.steps[nextIndex].spotId)
    }
  },

  endTutorial: () => {
    const { activeSeries, completedSeriesIds, onTutorialComplete } = get()
    const seriesId = activeSeries?.id
    const nextCompleted = seriesId && !completedSeriesIds.includes(seriesId)
      ? [...completedSeriesIds, seriesId]
      : completedSeriesIds
    set({ activeSeries: null, currentStepIndex: 0, isActive: false, completedSeriesIds: nextCompleted })
    if (seriesId && onTutorialComplete) {
      Promise.resolve(onTutorialComplete(seriesId)).catch(e => {
        console.error('[Tutorial] onTutorialComplete failed', e)
      })
    }
  },

  isTutorialCompleted: (seriesId: string) => {
    return get().completedSeriesIds.includes(seriesId)
  },

  setCompletedSeriesIds: (ids: string[]) => {
    set({ completedSeriesIds: ids })
  },

  setOnTutorialComplete: (cb: TutorialCompleteCallback | null) => {
    set({ onTutorialComplete: cb })
  },

  registerSpot: (id: string, element: HTMLElement) => {
    spotElements.set(id, element)
    const { activeSeries, currentStepIndex } = get()
    const currentStep = activeSeries?.steps[currentStepIndex]
    console.log('[Tutorial] registerSpot:', id, '| current step expects:', currentStep?.spotId, '| isActive:', get().isActive)
    if (currentStep?.spotId === id) {
      set({ spotsVersion: get().spotsVersion + 1 })
    }
  },

  unregisterSpot: (id: string) => {
    spotElements.delete(id)
    const { activeSeries, currentStepIndex } = get()
    const currentStep = activeSeries?.steps[currentStepIndex]
    if (currentStep?.spotId === id) {
      set({ spotsVersion: get().spotsVersion + 1 })
    }
  },

  getSpotElement: (id: string) => {
    return spotElements.get(id)
  },
}))
